/**
 * The "raise a credit note from this invoice" control, in the two layouts the
 * invoices list has. Moved here verbatim from `InvoicesPage` (2026-10-12) so
 * the company page's "Sales & billing history" mounts the same control rather
 * than a copy that could come to disagree about the gate.
 *
 * ⚠️ **ONE COMPONENT, TWO VARIANTS, for the reason `DownloadPdfButton` gives
 * for the same arrangement**: the desktop cell and the sub-`md` card are the
 * same list at two widths, and a control offered on one and not the other is
 * how somebody on a phone concludes the feature does not exist. Two call sites
 * building their own button is how they come to disagree about the gate.
 *
 * 🔴 **THE DISABLED REASON RIDES ON THE BUTTON ITSELF** — `tooltip` on the
 * row variant, `title` on the card variant. `Button` puts a disabled or
 * loading button's `tooltip ?? title` on a wrapper that receives the hover,
 * so the call site needs no span of its own.
 * *SUPERSEDED 2026-09-24: "THE DISABLED REASON IS ON A WRAPPER `<span>`, NOT
 * ON THE BUTTON … a `title` on it never renders at all … do not move it
 * back."* True before `Button` learnt to wrap a disabled control itself; a
 * span `title` here would now show a second label beside the tooltip.
 *
 * ⚠️ **The sentence is a why-this-is-disabled refusal, which is one of the
 * four documented exceptions to the no-helper-text prohibition.** It is not
 * printed beside the control — it rides on `title` — because a dense list
 * would otherwise carry one repetition per row.
 */

import { PlusIcon } from '@/components/catalog/icons';
import { Button } from '@/components/ui';
import type { CreditNoteGate } from '@/lib/credit-notes';
import type { Invoice } from '@/lib/types';

export function RaiseCreditNoteButton({
  invoice,
  gate,
  variant,
  busy,
  onRaise,
}: {
  invoice: Invoice;
  gate: CreditNoteGate;
  variant: 'row' | 'card';
  /**
   * The already-credited lookup for THIS invoice is in flight.
   *
   * ⚠️ Per invoice rather than per page: a shared flag would spin every
   * control in the list on one press. A round trip with no feedback at all
   * reads as a dead button and gets pressed again.
   */
  busy: boolean;
  onRaise: (invoice: Invoice) => void;
}) {
  const label = `Raise credit note for ${invoice.subject}`;
  return variant === 'row' ? (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      leadingIcon={<PlusIcon />}
      aria-label={label}
      tooltip={gate.allowed ? label : gate.reason}
      disabled={!gate.allowed}
      loading={busy}
      onClick={() => onRaise(invoice)}
    />
  ) : (
    <Button
      variant="secondary"
      size="sm"
      leadingIcon={<PlusIcon />}
      aria-label={label}
      title={gate.allowed ? label : gate.reason}
      disabled={!gate.allowed}
      loading={busy}
      onClick={() => onRaise(invoice)}
    >
      Credit note
    </Button>
  );
}
